<template>
  <v-dialog v-model="showDialog" persistent>
    <v-card outlined class="pa-3" v-if="loaded">
      <v-col class="text-right px-4">
        <v-btn icon @click="closeDialog">
          <v-icon>close</v-icon>
        </v-btn>
      </v-col>

      <v-card-title>
        <h6 class="mb-0 font-weight-bold">{{ mergePreview.facility.name }}</h6>
      </v-card-title>
      <v-card-text>
        <v-row>
          <!-- Left side -->
          <v-col>
            <LabelField
                :label="$t('common:facility-number')"
                :value="mergePreview.facility.objectNumber ? mergePreview.facility.objectNumber : '-'"
                flex
            />

            <LabelField
                :label="$t('settings/settings:duplicate.common.stairway')"
                :value="mergePreview.facility.stairway ? mergePreview.facility.stairway : '-'"
                flex
            />

            <LabelField
                :label="$t('common:type')"
                :value="mergePreview.facility.typeName ? mergePreview.facility.typeName : '-'"
                flex
            />

            <LabelField
                :label="$t('common:created_at')"
                :value="mergePreview.facility.createdAt"
                flex
            />

            <LabelField
                :label="$t('common:last-updated-on')"
                :value="mergePreview.facility.updatedAt"
                flex
            />

            <v-checkbox
                disabled
                v-model="mergePreview.facility.fullService"
                :label="$t('settings/settings:duplicate.preview.facility.full-service')"
            />

            <h6 class="mt-3 mb-0 font-weight-bold">{{ $t('common:building') }}</h6>
            <p class="mb-0">{{ mergePreview.facility.buildingName }}</p>
            <p class="mb-0">{{ mergePreview.facility.buildingStreet }}</p>
            <p class="mb-0">{{ mergePreview.facility.buildingZip }} {{ mergePreview.facility.buildingCity }}</p>
          </v-col>

          <!-- Right side -->
          <v-col>
            <OpenStreetMap
                v-show="mergePreview.facility.buildingLatitude != null && mergePreview.facility.buildingLongitude"
                ref="map"
                height="250"
                :lat="mergePreview.facility.buildingLatitude"
                :long="mergePreview.facility.buildingLongitude"
            />
          </v-col>
        </v-row>

        <div class="my-5"></div>

        <h6 class="mb-0 font-weight-bold">{{ $t('common:documentation_plural') }}</h6>
        <DataTable
            :headers="documentationTable"
            :data="mergePreview.documentations"
            icon="visibility"
            @clickedButton1="showDocument"
            sort-by="translatedType"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('common:contract_plural') }}</h6>
        <DataTable
            :headers="contractTable"
            :data="mergePreview.contracts"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('common:role_plural') }}</h6>
        <DataTable
            :headers="assignmentTable"
            :data="mergePreview.assignments"
            checkbox-on-property="accepted"
        />

        <h6 class="mb-0 font-weight-bold">{{ $t('settings/settings:duplicate.preview.facility.process-jobs') }}</h6>
        <DataTable
            :headers="processJobTable"
            :data="mergePreview.processJobs"
            checkbox-on-property="accepted"
            checkbox-on-property2="finished"
            checkbox-on-property3="lastResult"
            checkbox-on-property4="deleted"
        />

        <BaseButton
            :btn-text="$t('settings/settings:duplicate.preview.confirm-merge')"
            color="warning"
            @clickedButton="$refs.warningDialog.openDialog()"
        />
      </v-card-text>
    </v-card>

    <MergeWarningDialog
        ref="warningDialog"
        :objects-to-remove-names="secondaryFacilityNames"
        :object-translation="$t('common:facility_plural')"
        @confirmMerge="confirmMerge"
    />

    <DocumentViewer ref="documentViewer"/>
  </v-dialog>
</template>

<script>
import BaseButton from "@/modules/core/components/Base/BaseButton";
import LabelField from "@/modules/core/components/LabelField";
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";
import OpenStreetMap from "@/modules/core/components/OpenStreetMap/OpenStreetMap";
import DataTable from "@/modules/core/components/DataTable/DataTable";
import DocumentViewer from "@/modules/core/components/DocumentViewer/DocumentViewer";
import Utils from "@/modules/core/services/utils";
import MergeWarningDialog from "@/modules/settings/views/settings/duplicateMerge/MergeWarningDialog";
export default {
  name: "FacilityMerging",
  components: {MergeWarningDialog, DocumentViewer, DataTable, OpenStreetMap, LabelField, BaseButton},
  data() {
    return {
      mergePreview: {},
      loaded: false,
      mainFacilityId: null,
      secondaryFacilityIds: [],
      secondaryFacilityNames: [],
      showDialog: false
    }
  },
  computed: {
    documentationTable() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:type'), value: 'translatedType'},
        {text: '', value: 'controls'}
      ]
    },

    contractTable() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:type'), value: 'transformedTypes'},
        {text: this.$t('settings/settings:duplicate.preview.facility.contract-table.start'), value: 'contractStart'},
        {text: this.$t('settings/settings:duplicate.preview.facility.contract-table.end'), value: 'contractEnd'}
      ]
    },

    assignmentTable() {
      return [
        {text: this.$t('common:role'), value: 'roleName'},
        {text: this.$t('settings/settings:duplicate.preview.facility.common.assigned-to', { name: this.$t('common:name')}), value: 'userName'},
        {text: this.$t('settings/settings:duplicate.preview.facility.common.assigned-to', { name: this.$t('common:email')}), value: 'userEmail'},
        {text: this.$t('common:rank'), value: 'rank'},
        {text: this.$t('settings/settings:duplicate.preview.facility.common.accepted'), value: 'checkbox'}
      ]
    },

    processJobTable() {
      return [
        {text: this.$t('common:process'), value: 'processName'},
        {text: this.$t('common:date'), value: 'date'},
        {text: this.$t('settings/settings:duplicate.preview.facility.common.assigned-to', { name: this.$t('common:user')}), value: 'assignedToName'},
        {text: this.$t('settings/settings:duplicate.preview.facility.common.assigned-to', { name: this.$t('common:role')}), value: 'assignedToRoleName'},
        {text: this.$t('settings/settings:duplicate.preview.facility.common.accepted'), value: 'checkbox'},
        {text: this.$t('settings/settings:duplicate.preview.facility.job-table.finished'), value: 'checkbox2'},
        {text: this.$t('settings/settings:duplicate.preview.facility.job-table.last-result'), value: 'checkbox3'},
        {text: this.$t('settings/settings:duplicate.preview.facility.job-table.deleted'), value: 'checkbox4'}
      ]
    },
    currentLang() {
      return this.$store.state.translation.language;
    },
  },
  methods: {
    closeDialog() {
      this.mergePreview = {};
      this.loaded = false;
      this.mainFacilityId = null;
      this.secondaryFacilityIds = [];
      this.secondaryFacilityNames = [];
      this.showDialog = false;
    },

    transformContractTypes() {
      if (this.mergePreview.contracts) {
        this.mergePreview.contracts.forEach((contract) => {
          contract.translatedTypes = [];
          contract.types.forEach((type) => {
            contract.translatedTypes.push(this.$t('enums:contract-types.' + type));
          })

          if (contract.translatedTypes) {
            let typeString;
            if (contract.translatedTypes.length > 1) {
              typeString = contract.translatedTypes[0] + ", ";

              for (let i = 1; i < contract.translatedTypes.length - 1; i++) {
                typeString += contract.translatedTypes[i] + ", ";
              }

              typeString += contract.translatedTypes[contract.translatedTypes.length - 1];
            } else {
              typeString = contract.translatedTypes[0];
            }

            contract.transformedTypes = typeString;
          }
        })
      }
    },

    recenterMap() {
      // recenter map if building has lat and long set
      if (this.$refs.map && this.mergePreview.facility.buildingLatitude && this.mergePreview.facility.buildingLongitude) {
        this.$refs.map.refreshMap();
        this.$refs.map.recenterMap(this.mergePreview.facility.buildingLatitude, this.mergePreview.facility.buildingLongitude)
      }
    },

    createPreview(duplicateGroup) {
      this.showDialog = true;

      let facilityIds = [];
      for (let facility of duplicateGroup.objects) {
        if (duplicateGroup.primaryObjectId !== facility.id) {
          facilityIds.push(facility.id);
          this.secondaryFacilityIds.push(facility.id);
          this.secondaryFacilityNames.push(facility.name);
        }
      }

      this.mainFacilityId = duplicateGroup.primaryObjectId;

      DuplicateCheckService.readFacilityMergePreview(duplicateGroup.primaryObjectId, facilityIds,
          (res) => {
            this.mergePreview = res.data;
            this.mergePreview.documentations.forEach((docu) => {
              docu.translatedType = this.$t('enums:facility-documentation-types.' + docu.type);
            })

            this.transformContractTypes();
            this.recenterMap();

            this.loaded = true;
          },
          (error) => {
            this.$logger.error("Failed reading facility merge preview, because: " + error);
            Utils.showFailureSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.failed-reading-preview'))
          }
      )
    },

    confirmMerge() {
      this.$refs.warningDialog.closeDialog();

      DuplicateCheckService.mergeFacilities(this.mainFacilityId, this.secondaryFacilityIds,
          () => {
            Utils.showSuccessSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.merge-successful', { name: this.$t('common:facility_plural')}));
            this.$emit('finishedMerge', this.secondaryFacilityIds)
            this.closeDialog();
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('settings/settings:duplicate.preview.snackbar.merge-failed', { name: this.$t('common:facility_plural')}));
            this.$logger.error("Failed merging buildings because: " + error);
          }
      )
    },

    showDocument(item) {
      this.$refs.documentViewer.initViewer(this.mergePreview.documentations, item.fileName)
    }
  },
  watch: {
    // TODO somehow translating the docu and contract types does not work
    currentLang() {
      if (this.showDialog) {
        Utils.sleep(500).then(() => {
          if (this.mergePreview.documentations) {
            this.mergePreview.documentations.forEach((docu) => {
              docu.translatedType = this.$t('enums:facility-documentation-types.' + docu.type);
            })
          }

          this.transformContractTypes();
          this.recenterMap();
        })
      }
    },
  }
}
</script>

<style scoped>

</style>